import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from './Icon';
import SearchService from '../services/SearchService';
import JellyfishLoader from './JellyfishLoader';
import './ExpandingSearch.css';

const ExpandingSearch = ({ onResultClick }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const resultsPanelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile search on location change
  useEffect(() => {
    setIsMobileSearchActive(false);
    document.body.style.overflow = '';
  }, [location]);

  // Handle mobile focus
  const handleInputFocus = () => {
    if (window.innerWidth <= 480) {
      setIsMobileSearchActive(true);
      // Prevent body scroll when search is open on mobile
      document.body.style.overflow = 'hidden';
    }
  };

  const closeMobileSearch = () => {
    setIsMobileSearchActive(false);
    setQuery('');
    setResults(null);
    document.body.style.overflow = '';
  };

  // Focus search input when '/' is pressed
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if '/' is pressed and user is not already typing in an input/textarea
      if (e.key === '/' && 
          document.activeElement?.tagName !== 'INPUT' && 
          document.activeElement?.tagName !== 'TEXTAREA' &&
          !e.ctrlKey && 
          !e.metaKey && 
          !e.altKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Update panel position to align with search input
  useEffect(() => {
    const updatePanelPosition = () => {
      if (resultsPanelRef.current && searchRef.current) {
        const searchContainer = searchRef.current.querySelector('.search-expanded-container');
        if (searchContainer) {
          const rect = searchContainer.getBoundingClientRect();
          resultsPanelRef.current.style.left = `${rect.left}px`;
        }
      }
    };

    if (results || isSearching) {
      updatePanelPosition();
      window.addEventListener('resize', updatePanelPosition);
      window.addEventListener('scroll', updatePanelPosition);
    }

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition);
    };
  }, [results, isSearching]);

  // Debounced search
  useEffect(() => {
    if (query.trim().length === 0) {
      setResults(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const searchResults = await SearchService.searchAll(query, 5);
        
        if (searchResults) {
          setResults(searchResults);
          
          // Track search query
          SearchService.trackSearchEvent('search_query', {
            query: query,
            queryLength: query.length,
            totalResults: searchResults.totalResults
          });
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query]);


  const handleResultClick = (result) => {
    // Track result click
    SearchService.trackSearchEvent('search_result_clicked', {
      query: query,
      resultType: result.type,
      resultId: result.id
    });

    // Close mobile search if active
    if (isMobileSearchActive) {
      closeMobileSearch();
    }

    // Navigate based on result type
    switch (result.type) {
      case 'protocol':
        // Navigate to the protocol creator's user page, protocols tab
        if (result.creatorId) {
          navigate(`/user/${result.creatorId}#protocols`);
        } else {
          // Fallback to protocol catalogue if no creator ID
          navigate('/protocols/catalogue');
        }
        break;
      case 'post':
        navigate('/home');
        break;
      case 'biohack':
        // Navigate to create protocol page with biohack info to open
        navigate('/protocols/create', { 
          state: { 
            openBiohackId: result.id,
            openBiohackName: result.name 
          } 
        });
        break;
      case 'user':
        navigate(`/user/${result.id}`);
        break;
      default:
        break;
    }

    setQuery('');
    setResults(null);
    setActiveIndex(-1);
    onResultClick && onResultClick(result);
  };

  const handleViewAllResults = () => {
    // You can create a dedicated search results page
    // For now, navigate to home
    SearchService.trackSearchEvent('search_view_all_clicked', {
      query: query,
      totalResults: results?.totalResults || 0
    });
    
    navigate('/home');
    setQuery('');
    setResults(null);
    setActiveIndex(-1);
    
    if (isMobileSearchActive) {
      closeMobileSearch();
    }
  };

  // Keyboard navigation for results
  const handleKeyDown = (e) => {
    if (!results || results.totalResults === 0) return;

    const allResults = [
      ...(results.users || []),
      ...(results.protocols || []),
      ...(results.biohacks || []),
      ...(results.posts || [])
    ];

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < allResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleResultClick(allResults[activeIndex]);
    }
  };

  const renderResultItem = (item, index, globalIndex) => {
    const isActive = globalIndex === activeIndex;

    return (
      <div
        key={`${item.type}-${item.id}`}
        className={`search-result-item ${isActive ? 'active' : ''}`}
        onClick={() => handleResultClick(item)}
        role="option"
        aria-selected={isActive}
      >
        {item.type === 'user' || (item.type === 'biohack' && item.image) ? (
          <div className="search-result-icon-large">
            {item.type === 'user' && (
              item.avatar ? (
                <img src={item.avatar} alt={item.username} className="search-result-avatar" />
              ) : (
                <div className="search-result-avatar-fallback">
                  {(item.username || 'U').charAt(0).toUpperCase()}
                </div>
              )
            )}
            {item.type === 'biohack' && item.image && (
              <img src={item.image} alt={item.name} className="search-result-biohack-image" />
            )}
          </div>
        ) : (
          <div className="search-result-icon-container">
            {item.type === 'protocol' && <Icon name="TrendingUp" className="search-result-icon" />}
            {item.type === 'biohack' && <Icon name="Zap" className="search-result-icon" />}
            {item.type === 'post' && <Icon name="MessageSquare" className="search-result-icon" />}
          </div>
        )}
        
        <div className="search-result-details">
          {item.type === 'protocol' && (
            <>
              <div className="search-result-title">{item.name}</div>
              <div className="search-result-subtitle">
                by {item.creator}
              </div>
            </>
          )}
          {item.type === 'biohack' && (
            <>
              <div className="search-result-title">{item.name}</div>
              <div className="search-result-subtitle">
                {item.description ? (
                  item.description.length > 50 ? 
                    `${item.description.substring(0, 50)}...` : 
                    item.description
                ) : (
                  item.category || 'Biohack'
                )}
              </div>
            </>
          )}
          {item.type === 'post' && (
            <>
              <div className="search-result-title">
                {item.content.substring(0, 60)}
                {item.content.length > 60 ? '...' : ''}
              </div>
              <div className="search-result-subtitle">
                by {item.username} • {item.likesCount} likes
              </div>
            </>
          )}
          {item.type === 'user' && (
            <>
              <div className="search-result-title">{item.username}</div>
              <div className="search-result-subtitle">User Profile</div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderResults = () => {
    if (isSearching) {
      return (
        <div className="search-results-loading">
          <JellyfishLoader />
        </div>
      );
    }

    if (!results || query.trim().length === 0) {
      return null;
    }

    if (results.totalResults === 0) {
      return (
        <div className="search-results-empty">
          <Icon name="Search" className="icon--lg" />
          <div className="search-empty-text">
            <div className="search-empty-title">No results found</div>
            <div className="search-empty-subtitle">Try a different search term</div>
          </div>
        </div>
      );
    }

    let globalIndex = 0;

    return (
      <div className="search-results-list" role="listbox">
        {results.users && results.users.length > 0 && (
          <div className="search-results-section">
            <div className="search-results-section-title">
              <Icon name="Users" className="icon--xs" />
              People
            </div>
            {results.users.map((item, idx) => renderResultItem(item, idx, globalIndex++))}
          </div>
        )}

        {results.protocols && results.protocols.length > 0 && (
          <div className="search-results-section">
            <div className="search-results-section-title">
              <Icon name="TrendingUp" className="icon--xs" />
              Protocols
            </div>
            {results.protocols.map((item, idx) => renderResultItem(item, idx, globalIndex++))}
          </div>
        )}

        {results.biohacks && results.biohacks.length > 0 && (
          <div className="search-results-section">
            <div className="search-results-section-title">
              <Icon name="Zap" className="icon--xs" />
              Biohacks
            </div>
            {results.biohacks.map((item, idx) => renderResultItem(item, idx, globalIndex++))}
          </div>
        )}

        {results.posts && results.posts.length > 0 && (
          <div className="search-results-section">
            <div className="search-results-section-title">
              <Icon name="MessageSquare" className="icon--xs" />
              Posts
            </div>
            {results.posts.map((item, idx) => renderResultItem(item, idx, globalIndex++))}
          </div>
        )}
      </div>
    );
  };

  // Render just the icon for mobile when inactive
  if (isMobile && !isMobileSearchActive) {
    return (
      <button 
        className="search-icon-btn"
        onClick={() => {
          // Scroll to top in the background before opening search
          window.scrollTo(0, 0);
          setIsMobileSearchActive(true);
          document.body.style.overflow = 'hidden';
          // Use setTimeout to ensure the input is rendered before focusing
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 50);
        }}
        aria-label="Search"
      >
        <Icon name="Search" className="icon--md" />
      </button>
    );
  }

  return (
    <div 
      className={`expanding-search expanded ${isMobileSearchActive ? 'mobile-active' : ''}`}
      ref={searchRef}
    >
      <div className="search-expanded-container">
        <div className="search-input-container">
          {isMobileSearchActive && (
            <button 
              className="search-back-btn"
              onClick={closeMobileSearch}
              aria-label="Back"
            >
              <Icon name="ArrowLeft" className="icon--sm" />
            </button>
          )}
          <Icon name="Search" className="search-input-icon icon--sm" />
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            aria-label="Search"
            aria-expanded="true"
            aria-controls="search-results"
            aria-activedescendant={activeIndex >= 0 ? `search-result-${activeIndex}` : undefined}
            role="combobox"
            aria-autocomplete="list"
          />
          {query && (
            <button
              className="search-clear-btn"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <Icon name="X" className="icon--sm" />
            </button>
          )}
          {isMobileSearchActive && (
             <button
              className="search-cancel-text-btn"
              onClick={closeMobileSearch}
            >
              Cancel
            </button>
          )}
        </div>

        {(results || isSearching || isMobileSearchActive) && (
          <div className="search-results-panel" id="search-results" ref={resultsPanelRef}>
            <div className="search-results-header">
              <div className="search-results-title">Search Results</div>
              <div className="search-results-actions">
                {results && results.totalResults > 0 && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    {results.totalResults} {results.totalResults === 1 ? 'result' : 'results'}
                  </span>
                )}
              </div>
            </div>
            <div className="search-results-content">
              {renderResults()}
            </div>
          </div>
        )}

        {/* Screen reader announcements */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {results && `${results.totalResults} results found for ${query}`}
        </div>
      </div>
    </div>
  );
};

export default ExpandingSearch;

